import React, { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useStoreState } from 'easy-peasy'
import { useHistory } from 'react-router-dom'
import {
  Box,
  VStack,
  Text,
  GridItem,
  InputGroup,
  InputLeftElement,
  Flex,
  Link,
  Input
} from '@chakra-ui/react'
import { Link as RLink } from 'react-router-dom'
import { LayoutGrid, SearchIcon } from '@blueprinthq/joy'
import { endpoints } from '@api'
import { useExperienceManager } from '@hooks'
import { useSessionControllerPostSession } from '@core/api'
import { throttle } from 'lodash'
import { Loading } from '@components'
import { PatientOption } from './components/patient-option'
import flagsmith from 'flagsmith'
import { FlagsmithFeatures } from '@constants/flagsmith'

const SelectClient = () => {
  const [searchInput, setSearchInput] = useState('')
  const history = useHistory()
  const { user } = useStoreState(state => state.auth)
  const { mutateAsync: createSessionDraft } = useSessionControllerPostSession()
  const {
    isDocumentationAutomationEnabled,
    isExtension
  } = useExperienceManager()

  const isInternalDemoOrg = flagsmith.hasFeature(
    FlagsmithFeatures.SHOW_IS_DEMO_CLIENTS_IN_START_SESSION
  )

  const { data: defaultList, isLoading: isDefaultListLoading } = useQuery(
    [endpoints.getPatientList.getCacheId()],
    () =>
      endpoints.getPatientList.request({
        page: 1,
        status: 'active',
        sortBy: 'patient_name',
        order: 'ASC',
        clinician_id: user.id,
        clinic_id: user.clinic_id
      }),
    {
      select: data => {
        return data.nodes
          .map(n => ({
            ...n,
            id: n.patient_id,
            first_name: n.first_name,
            last_name: n.last_name
          }))
          .filter(n => !n.is_demo || isInternalDemoOrg)
      },
      onSuccess: data => {
        if (
          (data.length === 0 || (data.length === 1 && data[0].is_demo)) &&
          user.organization.canEnrollClients
        ) {
          history.push('/start-session/new-client?signup=true')
        }
      }
    }
  )

  const { refetch, data: searchResults, isLoading: isSearchLoading } = useQuery(
    [
      endpoints.getPatientSearch.getCacheId(),
      'session_client_search',
      searchInput
    ],
    () =>
      endpoints.getPatientSearch.request({
        search: searchInput,
        include_discharged: false
      }),
    {
      enabled: false,
      initialData: [],
      select: data => {
        return data
          .map(p => ({
            ...p,
            id: p.id,
            first_name: p.first_name,
            last_name: p.last_name
          }))
          .filter(n => !n.is_demo || isInternalDemoOrg)
      }
    }
  )

  useEffect(() => {
    if (searchInput.length >= 1) {
      throttledSearch()
    }
  }, [searchInput])

  const throttledSearch = throttle(() => refetch(), 150)

  const handleClick = async patientId => {
    const sessionDraft = await createSessionDraft({
      data: {
        clientId: patientId
      }
    })

    history.push(`/patient/${patientId}/session/${sessionDraft.id}`)
  }

  const patients = searchInput.length ? searchResults : defaultList || []

  const addClientLink = isDocumentationAutomationEnabled
    ? '/start-session/new-client'
    : '/new-client'

  return (
    <LayoutGrid>
      <GridItem
        colSpan={{ base: 12, md: 6 }}
        colStart={{ base: 1, md: 4 }}
        p={{ base: 4, md: 0 }}
        {...(isExtension ? {} : { mb: '60px' })}
      >
        {isDefaultListLoading ? (
          <Box h="70vh" color="white">
            <Loading color="white" />
          </Box>
        ) : (
          <>
            <Text
              as="h1"
              fontSize="lg"
              color="white"
              fontWeight="bold"
              textAlign="center"
              mb={6}
            >
              Choose a client to start a session
            </Text>
            <Box>
              <InputGroup>
                <InputLeftElement
                  h="100%"
                  left={4}
                  pointerEvents="none"
                  children={<SearchIcon color="gray.300" />}
                />
                <Input
                  placeholder="Search for a client..."
                  bg="white"
                  h="64px"
                  border="none"
                  onChange={e => setSearchInput(e.target.value)}
                  pl={20}
                />
              </InputGroup>
            </Box>
            {isSearchLoading ? (
              <Box h="300px" color="white">
                <Loading color="white" />
              </Box>
            ) : (
              <>
                <VStack mt={6} spacing={1}>
                  {patients.map(p => (
                    <PatientOption
                      key={p.id}
                      firstName={p.first_name}
                      lastName={p.last_name}
                      onClick={() => handleClick(p.id)}
                      dateOfBirth={p.date_of_birth}
                      isDemo={p.is_demo}
                    />
                  ))}
                </VStack>
                {user.organization.canEnrollClients && (
                  <Flex py={6} color="white" justifyContent="center">
                    <Text>
                      Missing a client?{' '}
                      <Link as={RLink} to={addClientLink}>
                        Add a new client
                      </Link>
                    </Text>
                  </Flex>
                )}
              </>
            )}
          </>
        )}
      </GridItem>
    </LayoutGrid>
  )
}

export default SelectClient
